import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { toast } from 'sonner';
import {
  DatabaseBackup,
  Mail,
  FileSpreadsheet,
  Users,
  Ticket,
  FileText,
  Loader2,
  CheckCircle2,
  AlertCircle,
  ShieldCheck
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BackupPage() {
  const { getAuthHeader, employee, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [lastBackup, setLastBackup] = useState(null);

  const isAdminHead = employee?.role === 'Admin Head' || employee?.role === 'Super Admin';

  const handleGenerateBackup = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/backup/generate`, {
        method: 'POST',
        headers: getAuthHeader(),
        
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setLastBackup({
          timestamp: new Date().toLocaleString(),
          stats: data.stats,
          emailSent: data.email_sent !== false
        });
        
        if (data.email_sent === false) {
          toast.warning('Backup generated but email could not be sent. Check email configuration.');
        } else {
          toast.success(`Backup sent successfully to ${user?.email}`);
        }
      } else {
        toast.error(data.detail || 'Failed to generate backup');
      }
    } catch (error) {
      console.error('Backup error:', error);
      toast.error('Failed to generate backup');
    } finally {
      setLoading(false);
    }
  };

  if (!isAdminHead) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Card className="max-w-md">
            <CardContent className="pt-6 text-center">
              <ShieldCheck className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h2 className="text-xl font-semibold mb-2">Access Restricted</h2>
              <p className="text-muted-foreground">
                Only Admin Head can access the backup feature.
              </p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="backup-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Data Backup</h1>
          <p className="text-muted-foreground mt-1">
            Export all system data to Excel and receive via email
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {/* Backup Action Card */}
          <Card className="md:col-span-1">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DatabaseBackup className="w-5 h-5 text-primary" />
                Generate Backup
              </CardTitle>
              <CardDescription>
                Create a complete backup of all system data
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">Email Delivery</p>
                    <p className="text-xs text-muted-foreground">
                      Backup will be sent to: <span className="font-medium">{user?.email}</span>
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <FileSpreadsheet className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">Excel Format</p>
                    <p className="text-xs text-muted-foreground">
                      Single file with multiple sheets
                    </p>
                  </div>
                </div>
              </div>

              <Button 
                className="w-full" 
                size="lg"
                onClick={handleGenerateBackup}
                disabled={loading}
                data-testid="generate-backup-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating Backup...
                  </>
                ) : (
                  <>
                    <DatabaseBackup className="w-4 h-4 mr-2" />
                    Generate & Send Backup
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* What's Included Card */}
          <Card className="md:col-span-1">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-primary" />
                What's Included
              </CardTitle>
              <CardDescription>
                Data exported in the backup file
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-3 rounded-lg border bg-card">
                  <div className="p-2 rounded-md bg-blue-100 dark:bg-blue-900/30">
                    <Ticket className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">Tickets</p>
                    <p className="text-xs text-muted-foreground">
                      All ticket records with status, assignee, expenses, and custom fields
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 rounded-lg border bg-card">
                  <div className="p-2 rounded-md bg-green-100 dark:bg-green-900/30">
                    <Users className="w-4 h-4 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">Employees</p>
                    <p className="text-xs text-muted-foreground">
                      All employee records with department, role, and manager info
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 rounded-lg border bg-card">
                  <div className="p-2 rounded-md bg-purple-100 dark:bg-purple-900/30">
                    <FileText className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">Request Types</p>
                    <p className="text-xs text-muted-foreground">
                      Custom request type configurations and form fields
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Last Backup Result */}
        {lastBackup && (
          <Alert className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <AlertTitle className="text-green-800 dark:text-green-200">Backup Generated Successfully</AlertTitle>
            <AlertDescription className="text-green-700 dark:text-green-300">
              <div className="mt-2 space-y-1">
                <p><strong>Time:</strong> {lastBackup.timestamp}</p>
                <p><strong>Records exported:</strong></p>
                <ul className="list-disc list-inside ml-2">
                  <li>{lastBackup.stats?.tickets || 0} Tickets</li>
                  <li>{lastBackup.stats?.employees || 0} Employees</li>
                  <li>{lastBackup.stats?.request_types || 0} Request Types</li>
                </ul>
                {lastBackup.emailSent ? (
                  <p className="mt-2">Check your email inbox for the backup file.</p>
                ) : (
                  <p className="mt-2 text-amber-600">Note: Email delivery failed. Please check email configuration.</p>
                )}
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Info Alert */}
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Important</AlertTitle>
          <AlertDescription>
            The backup file contains sensitive organizational data. Please ensure you store it securely 
            and only share it with authorized personnel.
          </AlertDescription>
        </Alert>
      </div>
    </Layout>
  );
}
